import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SlideOver from '../components/SlideOver';
import { apiFetch } from '../lib/api';
import { HelpPopover } from '../components/help';
import ProjectClientApprovalOverridePanel from '../components/clientApprovals/ProjectClientApprovalOverridePanel';

interface ApprovalConfig {
  id?: string;
  projectId: string;
  approvalMode: string;
  revocationWindowHours: number;
  acknowledgementWindowHours: number;
  waiversActive: boolean;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface Waiver {
  id: string;
  projectId: string;
  artefactType: string;
  waiverReason: string;
  waivedBy: string;
  waivedAt: string;
  expiresAt: string | null;
}

const ARTEFACT_TYPE_LABELS: Record<string, string> = {
  prd: 'PRD',
  sprint_backlog: 'Sprint Backlog',
  qa_report: 'QA Summary',
  deployment_runbook: 'Deployment',
};

const WINDOW_OPTIONS = [24, 48, 72];

export default function ApprovalConfigPage() {
  // Sprint A.9 — parent route now uses :projectId directly; drop the alias.
  const { projectId } = useParams<{ projectId: string }>();
  const [config, setConfig] = useState<ApprovalConfig | null>(null);
  const [waivers, setWaivers] = useState<Waiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Waiver modal state
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [waiverType, setWaiverType] = useState('prd');
  const [waiverReason, setWaiverReason] = useState('');
  const [waiverExpiry, setWaiverExpiry] = useState('');
  const [waiverSaving, setWaiverSaving] = useState(false);

  // Local form state
  const [approvalMode, setApprovalMode] = useState('ROLE_BASED');
  const [revocationHours, setRevocationHours] = useState(24);
  const [ackHours, setAckHours] = useState(24);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    try {
      const [configData, waiversData] = await Promise.all([
        apiFetch<ApprovalConfig>(`/projects/${projectId}/approval-config`),
        apiFetch<Waiver[]>(`/projects/${projectId}/approval-config/waivers`),
      ]);
      setConfig(configData);
      setWaivers(waiversData);
      setApprovalMode(configData.approvalMode);
      setRevocationHours(configData.revocationWindowHours);
      setAckHours(configData.acknowledgementWindowHours);
    } catch {
      setError('Failed to load approval configuration');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await apiFetch<ApprovalConfig>(`/projects/${projectId}/approval-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // #3185 APPROVE.2 2c: the three client toggles are no longer sent
          // (2b made them optional; the stored values are kept). Client
          // approval is the override panel below.
          approvalMode,
          revocationWindowHours: revocationHours,
          acknowledgementWindowHours: ackHours,
        }),
      });
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantWaiver = async () => {
    if (!projectId) return;
    setWaiverSaving(true);
    try {
      await apiFetch(`/projects/${projectId}/approval-config/waiver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artefactType: waiverType,
          waiverReason,
          ...(waiverExpiry ? { expiresAt: new Date(waiverExpiry).toISOString() } : {}),
        }),
      });
      setShowWaiverModal(false);
      setWaiverReason('');
      setWaiverExpiry('');
      await fetchData();
    } catch {
      setError('Failed to grant waiver');
    } finally {
      setWaiverSaving(false);
    }
  };

  const handleRevokeWaiver = async (waiverId: string) => {
    if (!projectId) return;
    try {
      await apiFetch(`/projects/${projectId}/approval-config/waiver/${waiverId}`, { method: 'DELETE' });
      await fetchData();
    } catch {
      setError('Failed to revoke waiver');
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-sm)',
    padding: 24,
    marginBottom: 16,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--text)',
    marginBottom: 4,
  };

  const descStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--text-secondary)',
    marginBottom: 16,
  };

  const btnPrimary: React.CSSProperties = {
    padding: '8px 20px',
    borderRadius: 'var(--radius-md)',
    background: 'var(--accent)',
    color: '#fff',
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
  };

  const btnGhost: React.CSSProperties = {
    padding: '6px 14px',
    borderRadius: 'var(--radius-md)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    fontWeight: 500,
    fontSize: 13,
    cursor: 'pointer',
  };

  if (loading) {
    return (
      <div className="flex-1 px-8 py-6">
        <p style={{ color: 'var(--text-secondary)' }}>Loading approval configuration...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 px-8 py-6">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Link to={`/projects/${projectId}`} style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none' }}>
            &larr; Back to project
          </Link>
        </div>

        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginBottom: 24 }}>
          Approval Configuration
        </h1>

        <div style={{ maxWidth: 640 }}>

          {error && (
            <div style={{ ...cardStyle, borderColor: '#ef4444', background: '#fef2f2', color: '#991b1b', marginBottom: 16 }}>
              {error}
            </div>
          )}

          {/* Section 1: Approval Mode */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 style={labelStyle}>Approval Mode</h2>
              <HelpPopover
                id="approval-mode-overview"
                variant="info"
                heading="Choosing your approval mode"
                body={
                  <>
                    <p style={{ margin: '0 0 8px' }}>
                      Approval mode controls how artefact approvals are gated for this project.
                    </p>
                    <p style={{ margin: '0 0 6px' }}>
                      <strong>ROLE_BASED</strong> — approvals are routed through configured roles.
                    </p>
                    <p style={{ margin: '0 0 8px' }}>
                      <strong>SINGLE_APPROVER</strong> — one designated approver handles all artefact approvals.
                    </p>
                    <p style={{ margin: '0 0 8px' }}>
                      Which deliverables need your client&apos;s approval is set separately, in the workspace&apos;s client approval settings and the project override below. It applies in either mode.
                    </p>
                    <p style={{ margin: 0 }}>
                      Switching modes mid-project is allowed and takes effect immediately for new approvals. Approvals already granted under the old mode stay valid.
                    </p>
                  </>
                }
              />
            </div>
            <p style={descStyle}>Choose how artefact approvals are enforced for this project.</p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="approvalMode"
                  value="SINGLE_APPROVER"
                  checked={approvalMode === 'SINGLE_APPROVER'}
                  onChange={() => setApprovalMode('SINGLE_APPROVER')}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>Single Approver</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Any PRODUCT_MANAGER on this project can approve artefacts. No role-based enforcement.
                  </div>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="approvalMode"
                  value="ROLE_BASED"
                  checked={approvalMode === 'ROLE_BASED'}
                  onChange={() => setApprovalMode('ROLE_BASED')}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>Role-Based</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    Approvals are enforced by role. The correct role must be assigned before approval is permitted.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Section 2: Client approvals for this project — #3185 APPROVE.2 2c,
              replacing the three require-client toggles (recon §6). Its own
              endpoint and save; admin rank, or FACULTY_SUPERVISOR in ACADEMIC. */}
          {projectId && (
            <ProjectClientApprovalOverridePanel projectId={projectId} cardStyle={cardStyle} labelStyle={labelStyle} descStyle={descStyle} />
          )}

          {/* Section 3: Windows */}
          <div style={cardStyle}>
            <h2 style={labelStyle}>Revocation &amp; Acknowledgement Windows</h2>
            <p style={descStyle}>Configure time windows for approval revocation and business-impact acknowledgements.</p>

            <div style={{ display: 'flex', gap: 24 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  Revocation window
                </label>
                <select
                  value={revocationHours}
                  onChange={(e) => setRevocationHours(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }}
                >
                  {WINDOW_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h} hours</option>
                  ))}
                </select>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Time window in which a primary approver can revoke a delegate&apos;s approval.
                </p>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  Acknowledgement window
                </label>
                <select
                  value={ackHours}
                  onChange={(e) => setAckHours(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }}
                >
                  {WINDOW_OPTIONS.map((h) => (
                    <option key={h} value={h}>{h} hours</option>
                  ))}
                </select>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Time window for business-impact acknowledgements before silence = acknowledged.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4: Waivers */}
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={labelStyle}>Active Waivers</h2>
                <HelpPopover
                  id="waiver-grant-overview"
                  variant="warning"
                  heading="When to grant a waiver"
                  body={
                    <>
                      <p style={{ margin: '0 0 8px' }}>
                        Waivers bypass role-based approval enforcement for a specific artefact type. Use them when role assignment is wrong for a one-off case, not as a substitute for fixing the underlying role configuration.
                      </p>
                      <p style={{ margin: '0 0 8px' }}>
                        Every waiver is logged in the audit trail with the granter, reason, scope, and expiry. Granted waivers stay in effect until expiry; revoking a waiver mid-flight stops new approvals from being granted under it but does not invalidate approvals already given.
                      </p>
                      <p style={{ margin: 0 }}>
                        If you&apos;re tempted to grant a waiver more than once for the same situation, that&apos;s a signal the role configuration itself needs to change.
                      </p>
                    </>
                  }
                />
              </div>
              <button style={btnGhost} onClick={() => setShowWaiverModal(true)}>
                Grant Waiver
              </button>
            </div>
            <p style={descStyle}>Waivers bypass role enforcement for a specific artefact type. All waivers are logged in the audit trail.</p>

            {waivers.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No active waivers.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>Reason</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500 }}>Expires</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {waivers.map((w) => (
                    <tr key={w.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 4px', color: 'var(--text)' }}>
                        {ARTEFACT_TYPE_LABELS[w.artefactType] ?? w.artefactType}
                      </td>
                      <td style={{ padding: '8px 4px', color: 'var(--text)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {w.waiverReason}
                      </td>
                      <td style={{ padding: '8px 4px', color: 'var(--text-secondary)' }}>
                        {w.expiresAt ? new Date(w.expiresAt).toLocaleDateString() : 'Permanent'}
                      </td>
                      <td style={{ padding: '8px 4px', textAlign: 'right' }}>
                        <button
                          style={{ ...btnGhost, color: '#ef4444', borderColor: '#ef4444', fontSize: 12, padding: '4px 10px' }}
                          onClick={() => handleRevokeWaiver(w.id)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Save button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
            {saved && <span style={{ fontSize: 13, color: '#10b981' }}>Saved successfully.</span>}
            {config?.updatedAt && (
              <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
                Last updated {new Date(config.updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* Waiver SlideOver */}
        <SlideOver
          isOpen={showWaiverModal}
          onClose={() => { setShowWaiverModal(false); setWaiverReason(''); setWaiverExpiry(''); }}
          title="Waiver Details"
          subtitle="Grant a waiver to bypass role enforcement for an artefact type."
          width="md"
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={btnGhost} onClick={() => { setShowWaiverModal(false); setWaiverReason(''); setWaiverExpiry(''); }}>
                Cancel
              </button>
              <button
                style={{ ...btnPrimary, opacity: waiverReason.length < 20 || waiverSaving ? 0.5 : 1 }}
                disabled={waiverReason.length < 20 || waiverSaving}
                onClick={handleGrantWaiver}
              >
                {waiverSaving ? 'Granting...' : 'Grant Waiver'}
              </button>
            </div>
          }
        >
              <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 'var(--radius-md)', padding: 12, marginBottom: 16, fontSize: 13, color: '#92400e' }}>
                A waiver bypasses role enforcement for this artefact type. The waiver reason will appear in the audit log.
              </div>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Artefact Type</label>
              <select
                value={waiverType}
                onChange={(e) => setWaiverType(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14, marginBottom: 16 }}
              >
                {Object.entries(ARTEFACT_TYPE_LABELS).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Reason (min 20 characters)</label>
              <textarea
                value={waiverReason}
                onChange={(e) => setWaiverReason(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14, marginBottom: 16, resize: 'vertical' }}
              />

              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Expiry Date (optional)</label>
              <input
                type="date"
                value={waiverExpiry}
                onChange={(e) => setWaiverExpiry(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 14 }}
              />
        </SlideOver>
    </div>
  );
}
